import React, { FC, useCallback, useEffect } from 'react'
import { Grid, InputLabel, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { UpdateUserDetailsRequestDto, CurrenciesFiat } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { makeStyles } from '@mui/styles'
import { Modal } from '../../../../components/Modal'
import { InputField } from '../../../../components/InputField'
import { DataPicker } from '../../../../components/DatePicker'
import CountrySelect from '../../../../components/CountrySelect'
import { InputPhone } from '../../../../components/InputPhone'
import { InputSelect } from '../../../../components/InputSelect'
import { Colors } from '../../../../theme'
import { IData } from '../../UserProfileTabs'
import { canUpdateUserData, updateUserDetailsRequest } from '../../../../store/actions'
import { canUpdateUserDataSelector, fiatsEnumSelector } from '../../../../store/selectors'

interface UserDetailsUpdateModalProps {
  isOpen: boolean
  modalClose: () => void
  data?: IData
  onSuccess?: () => void
}

const useStyles = makeStyles(() => ({
  formContent: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    margin: '32px 32px 32px 0',
    padding: 32,
    background: 'rgba(216, 216, 216, 0.05)',
  },
  inputWrap: {
    justifyContent: 'space-between',
    alignItems: 'stretch',
    flexWrap: 'wrap',
  },
  birthdayWrap: {
    maxHeight: 63,
  },
  birthdayInput: {
    fontSize: '14px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  switch: {
    display: 'flex',
    width: 300,
    height: 40,
    marginTop: 23,
    marginBottom: 23,
    '&>div': {
      alignSelf: 'center',
      margin: '0 auto',
    },
  },
  userPhoneError: {
    marginTop: -20,
    width: 300,
    color: Colors.error,
  },
}))

export const UserDetailsUpdateModal: FC<UserDetailsUpdateModalProps> = ({ isOpen, modalClose, data, onSuccess }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const fiatsEnum = useSelector(fiatsEnumSelector)
  const canUpdate = useSelector(canUpdateUserDataSelector)

  const formMethods = useForm<UpdateUserDetailsRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(UpdateUserDetailsRequestDto),
    defaultValues: {
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.email,
      birthday: data?.birthday,
      country: data?.country,
      city: data?.city,
      phone: data?.phone,
      currency: data?.currency as CurrenciesFiat,
    },
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty },
  } = formMethods

  useEffect(() => {
    if (canUpdate) {
      dispatch(canUpdateUserData(false))
      if (onSuccess) onSuccess()
      modalClose()
    }
  }, [canUpdate, dispatch, onSuccess, modalClose])

  const onSubmit = useCallback(
    (payload: UpdateUserDetailsRequestDto) => {
      if (data?.idSf) {
        dispatch(updateUserDetailsRequest({ ...payload, idSf: data?.idSf }))
      }
    },
    [data?.idSf, dispatch],
  )

  return (
    <Modal
      title={'Update User Details'}
      submitTitle="Update"
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid || !isDirty}
      width={670}
    >
      <FormProvider {...formMethods}>
        <Grid item container className={classes.inputWrap}>
          <InputField inputName={'firstName'} labelName={'First Name'} inputType={'text'} />
          <InputField inputName={'lastName'} labelName={'Last Name'} inputType={'text'} />
          <InputField inputName={'email'} labelName={'Email'} inputType={'text'} isCompanyEmail />
          <Typography component={'div'} className={classes.birthdayWrap}>
            <InputLabel className={classes.birthdayInput}>Date of Birth</InputLabel>
            <DataPicker
              width={300}
              margin={0}
              mt={3}
              dataPickerName={'birthday'}
              required={false}
              defaultValue={data?.birthday}
              showTodayButton={false}
            />
          </Typography>
          <CountrySelect
            inputName={'country'}
            labelName={'Country'}
            placeholder={'Please Select'}
            width={300}
            mb={25}
          />
          <InputField inputName={'city'} labelName={'City'} inputType={'text'} />
          <InputPhone defaultValue={data?.phone} />
          <InputSelect
            inputName={'currency'}
            labelName={'Currency'}
            width={300}
            placeholder={'Please Select'}
            mb={25}
            enums={fiatsEnum}
          />
        </Grid>
      </FormProvider>
    </Modal>
  )
}
